import React, { Suspense } from 'react';
import { useRouter } from 'next/router';
import { useWatch } from 'react-hook-form';
import { graphql, useLazyLoadQuery } from 'react-relay/hooks';
import { ErrorBoundary } from '@sparknz/journeys-common';

import { StoreListForOneOfferIdFieldQuery } from '__generated__/StoreListForOneOfferIdFieldQuery.graphql';
import { StoreListSection } from 'shared/components';
import { DEFAULT_STORE_FINDER_ELID } from 'shared/constants';
import { StoreItemViewModel } from 'shared/types/store';
import { createItemViewModel } from 'shared/utils/storeViewModel.utils';

type StoreListFieldProps = {
  setInfoLayerOpen: (open: boolean) => void;
  setSelectedStore: (store: StoreItemViewModel) => void;
};

export const StoreStockForOneOfferIdQuery = graphql`
  query StoreListForOneOfferIdFieldQuery(
    $offerIds: [String!]
    $limit: Int!
    $elid: String
  ) {
    storeStock(offerIds: $offerIds, limit: $limit, elid: $elid) {
      availability
      stores {
        id
        displayName
        addressLine1
        addressLine2
        emailAddress
        phoneNumber
        suburb
        city
        directions
        latitude
        longitude
        image
        stockStatus
        operatingHours {
          day
          open
          close
        }
      }
    }
  }
`;

const StoreListForOneOfferIdFieldInternal = ({
  setSelectedStore,
  setInfoLayerOpen,
}: StoreListFieldProps) => {
  const router = useRouter();
  const { offerId } = router.query;
  const InputElid = useWatch({
    name: 'queryElid',
  });
  const queryElid = InputElid || DEFAULT_STORE_FINDER_ELID;

  const {
    storeStock: { availability, stores: storesResponse },
  } = useLazyLoadQuery<StoreListForOneOfferIdFieldQuery>(
    StoreStockForOneOfferIdQuery,
    {
      offerIds: [offerId as string],
      limit: 5,
      elid: queryElid,
    },
  );

  const stores = storesResponse.map((store) => createItemViewModel(store));

  return (
    <StoreListSection
      stores={stores}
      setSelectedStore={setSelectedStore}
      setInfoLayerOpen={setInfoLayerOpen}
      availability={availability}
      displayRadio={false}
    />
  );
};

const StoreListForOneOfferIdField = ({
  setSelectedStore,
  setInfoLayerOpen,
}: StoreListFieldProps) => {
  return (
    <ErrorBoundary fallback={({ deslMessage }) => <div>{deslMessage}</div>}>
      <Suspense fallback={<div>Loading Store List...</div>}>
        <StoreListForOneOfferIdFieldInternal
          setSelectedStore={setSelectedStore}
          setInfoLayerOpen={setInfoLayerOpen}
        />
      </Suspense>
    </ErrorBoundary>
  );
};

export default StoreListForOneOfferIdField;
